import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    EMAIL_CALENDAR_COLUMNS,
    requiredIdentities,
} from '../../helpers/bulkSend'
import { canLinkVariables } from '../../helpers/linkVariables'
import { channelMeta } from '../PipelineWizard/wizardShared'
import { BRAND, FONT, RADIUS, SURFACE, TEXT } from '../../theme/tokens'
import Overline from './Overline'

/** Who a receiver type sends to, in words. */
const RECEIVER_LABELS = {
    __inline: 'recipients in the file',
    parents: 'parents',
    batch_student: 'batch students',
}

const receiverLabel = (r) => RECEIVER_LABELS[r] ?? r

/** One labelled line of the card. */
const Row = ({ label, children }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '190px 1fr' },
            gap: { xs: 0.25, sm: 2 },
            py: 1,
            borderTop: `1px solid ${SURFACE.border}`,
        }}
    >
        <Typography variant="caption" sx={{ color: TEXT.faint }}>
            {label}
        </Typography>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
)

Row.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node }

/** Body text of a row. */
const Value = ({ children }) => (
    <Typography
        variant="body2"
        sx={{ color: TEXT.secondary, overflowWrap: 'anywhere' }}
    >
        {children}
    </Typography>
)

Value.propTypes = { children: PropTypes.node }

/** Names in code type (template ids, column names), comma separated. */
const Names = ({ names }) => (
    <Box component="span" sx={{ fontFamily: FONT.mono }}>
        {names.join(', ')}
    </Box>
)

Names.propTypes = { names: PropTypes.arrayOf(PropTypes.string).isRequired }

/**
 * "What this event needs", shown once an event is picked: the channels and
 * templates its active pipelines send, the columns the file must have, the
 * template variables (one column each, unless linked) and, for email, the
 * optional calendar columns. From `/comms/v2/pipelines/event_type`; its
 * 404 ("Failed to get template by event type" when a pipeline's template is
 * missing) is shown as it is, with a retry.
 *
 * @param {object} props
 * @param {{loading: boolean, error: string, data: object|null}} props.details
 *     `data` is helpers/bulkSend `eventDetailsFrom`
 */
const EventRequirements = ({ eventType, details, onRetry }) => {
    if (details.loading)
        return (
            <Box
                role="status"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
            >
                <CircularProgress size={16} sx={{ color: BRAND.orange }} />
                <Typography variant="body2" sx={{ color: TEXT.muted }}>
                    Loading what this event needs…
                </Typography>
            </Box>
        )
    if (details.error)
        return (
            <Box sx={{ mt: 2 }}>
                <ErrorBanner
                    message={`Could not load what ${eventType} needs: ${details.error}`}
                />
                <Button variant="outlined" sx={{ mt: 1.5 }} onClick={onRetry}>
                    Try again
                </Button>
            </Box>
        )
    const data = details.data
    if (!data) return null
    const identities = requiredIdentities(data.comms).map((r) => r.label)
    return (
        <Box
            data-testid="event-requirements"
            sx={{
                mt: 3,
                p: 2,
                borderRadius: `${RADIUS.md}px`,
                background: SURFACE.subtle,
                border: `1px solid ${SURFACE.border}`,
            }}
        >
            <Overline sx={{ mb: 0.5 }}>What this event needs</Overline>
            <Row label="Channels">
                {data.channels.map((c) => (
                    <Value key={c.commType}>
                        {channelMeta(c.commType).label} to{' '}
                        {c.receiverTypes.map(receiverLabel).join(' and ')}
                        {c.templateIds.length > 0 && (
                            <>
                                {', '}
                                {c.templateIds.length === 1
                                    ? 'template '
                                    : 'templates '}
                                <Names names={c.templateIds} />
                            </>
                        )}
                    </Value>
                ))}
            </Row>
            <Row label="Columns the file must have">
                <Value>
                    {identities.length ? identities.join('; ') : 'None'}
                </Value>
            </Row>
            <Row label="Template variables">
                <Value>
                    {data.variables.length ? (
                        <>
                            <Names names={data.variables} />
                            {canLinkVariables(data)
                                ? ': a column of each name, or a link chosen on the next step'
                                : ': a column of each name'}
                        </>
                    ) : (
                        'None'
                    )}
                </Value>
            </Row>
            {data.hasEmail && (
                <Row label="Optional for email">
                    <Value>
                        <Names names={EMAIL_CALENDAR_COLUMNS} />
                        {', for the calendar invite'}
                    </Value>
                </Row>
            )}
            <Row label="When it sends">
                <Value>
                    {data.absoluteTime
                        ? 'At the date and time you pick below.'
                        : 'As soon as you press Send.'}
                    {data.niceness && ' Only between 8 AM and 8 PM.'}
                </Value>
            </Row>
        </Box>
    )
}

EventRequirements.propTypes = {
    eventType: PropTypes.string.isRequired,
    details: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.string,
        data: PropTypes.object,
    }).isRequired,
    onRetry: PropTypes.func.isRequired,
}

export default EventRequirements
